import React, { useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { CustomDialogTitle } from '../../Custom/CustomDialogTitle';
import { AppQuill } from '../../../../Component/Common/Components/FormValidationInputs/AppQuill';
import { Documents } from '../Documents/Documents';
import { yupResolver } from '@hookform/resolvers/yup';
import { supportFeedbackValidationSchema } from './SupportFeedback';
import { FieldValues, useForm } from 'react-hook-form';
import { UploadButton } from '../../../../Component/Common/Components/UploadButton/UploadButton';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { LoadingButton } from '@mui/lab';
import { Support } from '../../../../Component/Model/Dto/Support/SupportRequest';
import { toast } from 'react-toastify';
import { SupportRequestService } from '../../../../Component/Api/SupportRequest/SupportRequestService';
import { DocumentUploadParameters } from '../../../../Component/Model/Dto/Document';
import { DocumentUploadService } from '../../../../Component/Api/DocumentUpload/DocumentUploadService';

interface IProps {
    supportFeedbackId?: number;
    setSupportFeedbackId: React.Dispatch<React.SetStateAction<number | undefined>>;
    openModal: boolean;
    handleClose: () => void;
    allowEdit: boolean;
    scorecardId: string;
    supportTicketId: string;
}

export const SupportFeedbackModal = (props: IProps) => {

    const [documentsUploaded, setDocumentsUploaded] = React.useState<Support.TicketDocumentDto[]>([]);

    const { control, handleSubmit, formState: { isSubmitting, isValid }, reset, getValues, trigger } = useForm({
        mode: 'all',
        resolver: yupResolver(supportFeedbackValidationSchema)
    });

    const uploadFile = async (document: any) => {
        const headers = { 'Content-Type': 'multipart/form-data' };

        let formData = new FormData();
        formData.append(document.name, document);

        try {
            const feedback: Support.TicketFeedbackDtoIn = {
                Id: props.supportFeedbackId ?? 0,
                TicketId: parseInt(props.supportTicketId),
                TypeId: getValues("TypeId") ?? 1,
                Message: getValues("Message") ?? ""
            };
            const response = await SupportRequestService.SaveFeedback(feedback);
            const feedbackId = response.data;

            const documentUploadRequest: DocumentUploadParameters = {
                id: feedbackId.toString(),
                link: "TicketFeeback",
                scorecardId: props.scorecardId,
                documentTypeId: "148"
            };
            await DocumentUploadService.SupportingDocumentsFileUpload(formData, documentUploadRequest, headers);

            if (props.supportFeedbackId) {
                toast.success('Document uploaded');
                fetchData();
            } else {
                toast.success('Your feedback has been saved');
                props.handleClose();
            }

        } catch (error) {
        }
    }

    const onFileUpload = (event: any) => {
        if (event.target) {
            const document = event.target.files[0];
            const fileSizeInKB = Math.ceil(document.size / 1024);
            const maxFileSizeInKB = 1024 * 20;
            let fileExt = document.name.split('.').pop();
            const allowedFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'doc', 'docx', 'xls', 'xlsx', 'pdf', 'zip', 'rar']
            if (allowedFileTypes.includes(fileExt.toLowerCase())) {
                if (fileSizeInKB > maxFileSizeInKB) {
                    toast.error("File is bigger than 20MB");
                    return;
                }
                else {
                    uploadFile(document);
                    return;
                }
            }
            else {
                toast.error("Unsupported file type. Permitted file types are: .jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .xls, .xlsx, .pdf, .zip, .rar");
                return;
            }
        }
    }

    const fetchData = async () => {
        try {
            if (props.supportFeedbackId) {
                const response = await SupportRequestService.GetFeeback(props.supportFeedbackId);
                const dataObject = response.data;
                setDocumentsUploaded(dataObject.Documents);
                reset(dataObject);
            }
            trigger();
        } catch {
        }
    }

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const feedback: Support.TicketFeedbackDtoIn = {
                Id: props.supportFeedbackId ?? 0,
                TicketId: parseInt(props.supportTicketId),
                TypeId: data.TypeId ?? 1,
                Message: data.Message ?? ""
            };
            await SupportRequestService.SaveFeedback(feedback);
            toast.success('Your feedback has been saved');
            props.handleClose();
        } catch (error) {
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            <Dialog disableEnforceFocus open={props.openModal} onClose={props.handleClose} aria-labelledby="form-dialog-title" maxWidth='xl' fullWidth >
                <CustomDialogTitle id="customized-dialog-title" onClose={props.handleClose}>
                    {props.supportFeedbackId ? "Edit Support Feedback" : "Add Support Feedback"}
                </CustomDialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        <Grid item container xs={7} component="form" id='SupportFeedbackForm' onSubmit={handleSubmit(handleDataSubmit)}>
                            <Grid item xs={12}>
                                <AppQuill control={control} name="Message" placeholder="Please describe your query..." disabled={!props.allowEdit} />
                            </Grid>
                        </Grid>
                        <Grid item xs={5}>
                            <Documents
                                documentsUploaded={documentsUploaded}
                                allowEdit={props.allowEdit}
                                scorecardId={props.scorecardId}
                                afterDelete={() => fetchData()} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {props.allowEdit &&
                        <>
                            <UploadButton
                                id='upload-file' onChange={onFileUpload} type='file'
                                accept=".jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .xls, .xlsx, .pdf, .zip, .rar"
                                IconView={AttachFileIcon} text='Select file'
                                color="info"
                                disabled={!isValid}>
                            </UploadButton>
                            <LoadingButton disabled={!isValid} variant='contained' color='primary' type='submit' form='SupportFeedbackForm' loading={isSubmitting} sx={{ ml: 1 }}>
                                Save
                            </LoadingButton>
                        </>
                    }
                </DialogActions>
            </Dialog>
        </>);
}
